import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { subscriptionsApi } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import {
  MARKETPLACE_CONTACTS,
  MARKETPLACE_DEMO_PATH,
  MARKETPLACE_EXAMPLES,
  MARKETPLACE_FAQ,
  MARKETPLACE_FEATURES,
  MARKETPLACE_FINAL_CTA,
  MARKETPLACE_HERO,
  MARKETPLACE_LOGOS_STRIP,
  MARKETPLACE_NAV,
  MARKETPLACE_PAIN,
  MARKETPLACE_PRICING,
  MARKETPLACE_SEO,
} from './marketplaceLandingContent'
import {
  applyLandingSeo,
  captureUtmParams,
  getSiteUrl,
  initLandingAnalytics,
  trackLandingCta,
  useLandingDarkThemeEffect,
} from './landingUtils'
import { DEFAULT_LANDING_PLANS, mergeLandingPlans, type LandingPlanCard } from './landingPlans'
import { MARKETPLACE_HERO_PREVIEW } from './landingMedia'
import { trackEvent } from '../../utils/productAnalytics'
import BrandLogo from '../../components/BrandLogo/BrandLogo'
import './LandingPage.css'

export default function MarketplaceLandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [landingPlans, setLandingPlans] = useState<LandingPlanCard[]>(DEFAULT_LANDING_PLANS)
  const telegramUrl = import.meta.env.VITE_TELEGRAM_URL?.trim()
  const vkUrl = import.meta.env.VITE_VK_URL?.trim()

  useEffect(() => {
    captureUtmParams()
    initLandingAnalytics()
    applyLandingSeo({
      page: 'marketplace',
      title: MARKETPLACE_SEO.title,
      description: MARKETPLACE_SEO.description,
      path: MARKETPLACE_SEO.path,
      ogImage: getSiteUrl(MARKETPLACE_SEO.ogImage),
    })
  }, [])

  useEffect(() => {
    subscriptionsApi
      .getPlans()
      .then(res => {
        setLandingPlans(mergeLandingPlans(res.data.free, res.data.plans ?? []))
      })
      .catch(() => {})
  }, [])

  useEffect(useLandingDarkThemeEffect(), [])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  const enterDemo = (source: string) => {
    trackLandingCta('marketplace', source)
    trackEvent('guest_enter_app', { source, dest: MARKETPLACE_DEMO_PATH }, { surface: 'marketplace' })
    setMobileMenuOpen(false)
    navigate(MARKETPLACE_DEMO_PATH)
  }

  const handlePricingCta = (plan: LandingPlanCard) => {
    if (isAuthenticated) {
      trackLandingCta('marketplace', `pricing_${plan.key}`)
      navigate('/balance')
      return
    }
    enterDemo(`pricing_${plan.key}`)
  }

  const scrollToExamples = (source: string) => {
    trackLandingCta('marketplace', source)
    setMobileMenuOpen(false)
    document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="kmin bg-[#07070A] text-white min-h-screen font-sans overflow-hidden">
      <div className="kmin-glow">
        <div className="kmin-glow-top" />
        <div className="kmin-glow-bottom" />
      </div>

      <header className="kmin-nav">
        <div className="kmin-container">
          <div className="kmin-nav-inner">
            <BrandLogo variant="landing" to="/" />
            <nav className="kmin-nav-links" aria-label="Навигация: карточки для маркетплейса">
              {MARKETPLACE_NAV.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="kmin-nav-actions">
              <button
                type="button"
                className="kmin-nav-burger"
                aria-label={mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(v => !v)}
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
              <button
                type="button"
                className="kmin-btn-primary kmin-nav-cta"
                onClick={() => enterDemo('nav_try')}
              >
                {MARKETPLACE_HERO.primaryCta}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="kmin-nav-mobile-backdrop"
              aria-label="Закрыть меню"
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav className="kmin-nav-mobile-sheet" aria-label="Мобильное меню">
              {MARKETPLACE_NAV.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-block"
                onClick={() => enterDemo('nav_mobile_try')}
              >
                {MARKETPLACE_HERO.primaryCta}
              </button>
            </nav>
          </>
        )}
      </header>

      <section className="kmin-hero kmin-hero--designers">
        <div className="kmin-container">
          <div className="kmin-hero-grid">
            <div>
              <div className="kmin-badge">{MARKETPLACE_HERO.badge}</div>
              <h1 className="kmin-hero-title">
                <span style={{ color: '#ff3495' }}>{MARKETPLACE_HERO.title}</span>
              </h1>
              <p className="kmin-hero-desc">{MARKETPLACE_HERO.subtitle}</p>
              <div className="kmin-hero-actions">
                <button
                  type="button"
                  className="kmin-btn-primary kmin-btn-lg"
                  onClick={() => enterDemo('hero_primary')}
                >
                  {MARKETPLACE_HERO.primaryCta}
                </button>
                <button
                  type="button"
                  className="kmin-btn-outline kmin-btn-lg"
                  onClick={() => scrollToExamples('hero_secondary')}
                >
                  {MARKETPLACE_HERO.secondaryCta}
                </button>
              </div>
              <p className="kmin-hero-trial-badge">{MARKETPLACE_HERO.trialBadge}</p>
            </div>

            <div className="kmin-hero-preview">
              <div className="kmin-hero-preview-glow" />
              <div className="kmin-hero-preview-card">
                <div className="kmin-preview-header">
                  <div>
                    <div className="font-medium">Карточки товаров</div>
                    <div className="kmin-preview-sub">{MARKETPLACE_HERO.previewSub}</div>
                  </div>
                  <div className="kmin-preview-status">Онлайн</div>
                </div>
                <div className="kmin-preview-prompt">{MARKETPLACE_HERO.previewPrompt}</div>
                <div className="kmin-preview-grid2">
                  {MARKETPLACE_HERO_PREVIEW.large.map((src, i) => (
                    <div
                      key={src}
                      className="kmin-preview-box"
                      onClick={() => setPreviewUrl(src)}
                    >
                      <img
                        src={src}
                        alt={i === 0 ? 'Пример карточки товара' : 'Пример слайда маркетплейса'}
                        className="kmin-preview-img"
                        fetchPriority={i === 0 ? 'high' : undefined}
                        loading={i === 0 ? undefined : 'lazy'}
                      />
                    </div>
                  ))}
                </div>
                <div className="kmin-preview-grid3">
                  {MARKETPLACE_HERO_PREVIEW.small.map(src => (
                    <div
                      key={src}
                      className="kmin-preview-box-sm"
                      onClick={() => setPreviewUrl(src)}
                    >
                      {src.endsWith('.mp4') ? (
                        <video
                          src={src}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="kmin-preview-img-sm"
                        />
                      ) : (
                        <img
                          src={src}
                          alt="Пример визуала карточки"
                          className="kmin-preview-img-sm"
                          loading="lazy"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-logos">
        <div className="kmin-container">
          <div className="kmin-logos-inner kmin-logos-inner--tagline">
            <span>{MARKETPLACE_LOGOS_STRIP}</span>
          </div>
        </div>
      </section>

      <section id="examples" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-examples-header" style={{ marginBottom: 48 }}>
            <div>
              <div className="kmin-label">Примеры</div>
              <h2 className="kmin-section-title">
                Как выглядят <span className="block text-white/50">результаты</span>
              </h2>
            </div>
          </div>
          <div className="kmin-examples-grid">
            {MARKETPLACE_EXAMPLES.map(item => (
              <div key={item.title} className="kmin-example-card">
                <div
                  className="kmin-example-img"
                  onClick={() => setPreviewUrl(item.media)}
                >
                  {item.isVideo ? (
                    <video
                      src={item.media}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="kmin-preview-img"
                    />
                  ) : (
                    <img
                      src={item.media}
                      alt={item.title}
                      className="kmin-preview-img"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="kmin-example-body">
                  <div className="kmin-example-meta">
                    <span>Карточки товаров</span>
                    <span className="text-emerald-400">МП</span>
                  </div>
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-example-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pain" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Зачем</div>
            <h2 className="kmin-section-title">
              Карточки без <span className="block text-white/50">очереди к дизайнеру</span>
            </h2>
          </div>
          <div className="kmin-audience-grid">
            {MARKETPLACE_PAIN.map(item => (
              <div key={item.title} className="kmin-audience-card">
                <h3 className="kmin-feature-title">{item.title}</h3>
                <p className="kmin-feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{MARKETPLACE_PRICING.label}</div>
            <h2 className="kmin-section-title">{MARKETPLACE_PRICING.title}</h2>
            <p className="kmin-section-desc">{MARKETPLACE_PRICING.desc}</p>
          </div>
          <div className="kmin-pricing-grid">
            {landingPlans.map(plan => (
              <div
                key={plan.key}
                className={`kmin-plan-card ${plan.accent ? 'kmin-plan-card--accent' : ''}`}
              >
                <div className="kmin-plan-name">{plan.title}</div>
                <div className="kmin-plan-price">{plan.price}</div>
                <p className="kmin-plan-desc">{plan.desc}</p>
                <button
                  type="button"
                  className={
                    plan.accent ? 'kmin-btn-primary kmin-btn-block' : 'kmin-btn-outline kmin-btn-block'
                  }
                  onClick={() => handlePricingCta(plan)}
                >
                  {isAuthenticated
                    ? 'Выбрать тариф'
                    : plan.key === 'free'
                      ? 'Попробовать бесплатно'
                      : 'Выбрать'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Возможности</div>
            <h2 className="kmin-section-title">
              Всё для карточки <span className="block text-white/50">в одном месте</span>
            </h2>
          </div>
          <div className="kmin-features-grid">
            {MARKETPLACE_FEATURES.map(item => (
              <div key={item.title} className="kmin-feature-card">
                <div
                  className="kmin-feature-img"
                  onClick={() => setPreviewUrl(item.media)}
                >
                  {item.isVideo ? (
                    <video
                      src={item.media}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="kmin-preview-img"
                    />
                  ) : (
                    <img
                      src={item.media}
                      alt={`Пример: ${item.title}`}
                      className="kmin-preview-img"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Вопросы</div>
            <h2 className="kmin-section-title">Коротко по делу</h2>
          </div>
          <div className="kmin-audience-grid">
            {MARKETPLACE_FAQ.map(item => (
              <div key={item.q} className="kmin-audience-card">
                <h3 className="kmin-feature-title">{item.q}</h3>
                <p className="kmin-feature-desc">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-cta-card">
            <div className="kmin-cta-glow" />
            <div className="kmin-cta-content">
              <h3 className="kmin-cta-title">{MARKETPLACE_FINAL_CTA.title}</h3>
              <p className="kmin-cta-desc">{MARKETPLACE_FINAL_CTA.desc}</p>
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-cta"
                onClick={() => enterDemo('final_cta')}
              >
                {MARKETPLACE_FINAL_CTA.button}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="contacts" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-contacts-card">
            <div className="kmin-contacts-glow" />
            <div className="kmin-contacts-content">
              <div>
                <div className="kmin-label">Контакты</div>
                <h3 className="kmin-section-title">
                  {MARKETPLACE_CONTACTS.title}
                  <span className="block text-white/70">{MARKETPLACE_CONTACTS.titleRest}</span>
                </h3>
                <p className="kmin-section-desc">{MARKETPLACE_CONTACTS.desc}</p>
              </div>
              <div className="kmin-contacts-grid">
                <a
                  href={`mailto:${MARKETPLACE_CONTACTS.email}`}
                  className="kmin-contact-item"
                  onClick={() => trackLandingCta('marketplace', 'contacts_email')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">
                    mail
                  </span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">Email</span>
                    <span className="kmin-contact-value">{MARKETPLACE_CONTACTS.email}</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">
                    arrow_outward
                  </span>
                </a>
                <a
                  href={MARKETPLACE_CONTACTS.vk}
                  className="kmin-contact-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackLandingCta('marketplace', 'contacts_vk')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">
                    groups
                  </span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">ВКонтакте</span>
                    <span className="kmin-contact-value">{MARKETPLACE_CONTACTS.vkLabel}</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">
                    arrow_outward
                  </span>
                </a>
                <a
                  href={MARKETPLACE_CONTACTS.telegram}
                  className="kmin-contact-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackLandingCta('marketplace', 'contacts_telegram')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">
                    send
                  </span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">Telegram</span>
                    <span className="kmin-contact-value">{MARKETPLACE_CONTACTS.telegramLabel}</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">
                    arrow_outward
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="kmin-footer">
        <div className="kmin-container">
          <div className="kmin-footer-inner">
            <div>
              <BrandLogo variant="landing-sm" to="/" />
              <div className="kmin-footer-sub">AI-карточки для маркетплейсов</div>
              <div className="kmin-footer-legal">
                <div>ИП Вихарев С.В.</div>
                <div>ИНН: 745202549414</div>
                <div>ОГРНИП: 320237500261078</div>
              </div>
            </div>
            <div className="kmin-footer-links">
              <Link to="/" className="kmin-footer-link-btn">
                Главная
              </Link>
              <Link to="/designers" className="kmin-footer-link-btn">
                Для дизайнеров
              </Link>
              {telegramUrl && (
                <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
                  Telegram
                </a>
              )}
              {vkUrl && (
                <a href={vkUrl} target="_blank" rel="noopener noreferrer">
                  VK
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {previewUrl && (
        <div className="kmin-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button
            type="button"
            className="kmin-preview-close"
            onClick={() => setPreviewUrl(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
          {previewUrl.endsWith('.mp4') ? (
            <video
              src={previewUrl}
              autoPlay
              loop
              playsInline
              className="kmin-preview-full"
              controls
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={previewUrl}
              alt="Увеличенный предпросмотр"
              className="kmin-preview-full"
            />
          )}
        </div>
      )}
    </div>
  )
}
